import React, { useState, useEffect } from 'react';
import "./FinancialFaq.css";

const FinancialFaq = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [mouseX, setMouseX] = useState(0);
  const [ripples, setRipples] = useState([]);

  const faqs = [
    {
      id: "services",
      title: "Financial Services",
      question: "What financial services do you provide?",
      answer: "We offer a wide range of financial services to help both individuals and businesses secure their future.",
      items: [
        { icon: "📊", text: "Personal financial planning", desc: "budgeting, savings, retirement planning" },
        { icon: "💹", text: "Investment advisory", desc: "mutual funds, stocks, bonds, real estate guidance" },
        { icon: "💰", text: "Tax planning & optimization", desc: "maximize your returns legally" },
        { icon: "🛡️", text: "Insurance & risk management", desc: "protect your assets" },
        { icon: "🏢", text: "Business financial consultancy", desc: "corporate financial solutions" },
        { icon: "💎", text: "Wealth management", desc: "portfolio tracking & optimization" }
      ],
      highlight: "End-to-end financial solutions for every stage of your journey",
      color: "#FF6B6B"
    },
    {
      id: "investment",
      title: "Investment Amount",
      question: "Do I need a minimum investment amount?",
      answer: "No minimum required. We believe financial planning should be accessible to everyone.",
      items: [
        { icon: "🌱", text: "Start small with SIPs", desc: "systematic investment plans" },
        { icon: "📈", text: "Beginner strategies", desc: "simple wealth building approaches" },
        { icon: "💎", text: "Advanced management", desc: "for high-net-worth clients" },
        { icon: "🎯", text: "Flexible options", desc: "customize your investment journey" }
      ],
      highlight: "Start early, stay consistent - amount doesn't matter",
      color: "#4ECDC4"
    },
    {
      id: "safety",
      title: "Money Safety",
      question: "How do I know my money is safe?",
      answer: "Your financial safety is our top priority through regulated partnerships and transparent processes.",
      items: [
        { icon: "🏛️", text: "Regulated institutions", desc: "trusted and verified partners" },
        { icon: "🔍", text: "Transparent processes", desc: "no hidden charges or fees" },
        { icon: "🔐", text: "Secure platforms", desc: "advanced transaction security" },
        { icon: "📋", text: "Regular reviews", desc: "continuous portfolio monitoring" },
        { icon: "⚖️", text: "Risk management", desc: "proactive wealth protection" }
      ],
      highlight: "Highest standards of trust and professionalism guaranteed",
      color: "#45B7D1"
    }
  ];

  const createRipple = (x, y) => {
    const newRipple = { id: Date.now(), x, y };
    setRipples(prev => [...prev, newRipple]);
    setTimeout(() => {
      setRipples(prev => prev.filter(ripple => ripple.id !== newRipple.id));
    }, 800);
  };

  useEffect(() => {
    const handleMouseMove = (e) => setMouseX(e.clientX);
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="bg-white" style={{ minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {ripples.map(ripple => (
        <div
          key={ripple.id}
          className="position-fixed rounded-circle"
          style={{
            left: ripple.x - 25,
            top: ripple.y - 25,
            width: '50px',
            height: '50px',
            background: `radial-gradient(circle, ${faqs[activeTab].color}30, transparent)`,
            pointerEvents: 'none',
            animation: 'rippleExpand 0.8s ease-out forwards',
            zIndex: 1000
          }}
        />
      ))}

      <div className="container py-5">
        <div className="text-center mb-5">
          <div className="position-relative d-inline-block">
            <h1 
              className="display-5 fw-bold text-dark mb-0"
              style={{
                transform: `skew(${(mouseX - window.innerWidth/2) * 0.01}deg)`,
                transition: 'transform 0.1s ease-out'
              }}
            >
              FAQ
            </h1>
            <div 
              className="position-absolute bottom-0 start-0 w-100"
              style={{
                height: '4px',
                background: `linear-gradient(90deg, ${faqs[activeTab].color}, transparent)`,
                transform: 'scaleX(0)',
                transformOrigin: 'left',
                animation: 'scaleIn 0.5s ease forwards'
              }}
            />
          </div>
        </div>

        <div className="row justify-content-center mb-4">
          <div className="col-lg-10">
            <div 
              className="d-flex rounded-pill p-2 position-relative overflow-hidden faq-tab-nav"
              style={{
                background: 'linear-gradient(135deg, #f8f9ff, #e8f2ff)',
                border: '2px solid #e2e8f0',
                width: '100%',
                justifyContent: 'space-between'
              }}
            >
              <div 
                className="position-absolute top-0 h-100 rounded-pill"
                style={{
                  background: `linear-gradient(135deg, ${faqs[activeTab].color}, ${faqs[activeTab].color}cc)`,
                  width: `${100 / faqs.length}%`,
                  left: `${(activeTab * 100) / faqs.length}%`,
                  transition: 'all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
                  zIndex: 0
                }}
              />
              {faqs.map((faq, index) => (
                <button
                  key={index}
                  className="flex-fill btn border-0 py-3 px-4 position-relative fw-semibold text-center"
                  style={{
                    color: activeTab === index ? 'white' : '#64748b',
                    zIndex: 1,
                    fontSize: '15px',
                    transition: 'all 0.3s ease',
                    transform: activeTab === index ? 'scale(1.05)' : 'scale(1)'
                  }}
                  onClick={(e) => {
                    setActiveTab(index);
                    createRipple(e.clientX, e.clientY);
                  }}
                >
                  {faq.title}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div 
              className="rounded-4 p-5 position-relative overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${faqs[activeTab].color}08, transparent)`,
                border: `2px solid ${faqs[activeTab].color}30`,
                transition: 'all 0.5s ease'
              }}
            >
              <div 
                className="position-absolute"
                style={{
                  top: '-50%',
                  right: '-10%',
                  width: '300px',
                  height: '300px',
                  background: `conic-gradient(from 0deg, ${faqs[activeTab].color}10, transparent, ${faqs[activeTab].color}05)`,
                  borderRadius: '50%',
                  animation: 'rotate 20s linear infinite',
                  zIndex: 1
                }}
              />
              <div className="position-relative" style={{ zIndex: 2 }}>
                <div className="mb-5">
                  <div 
                    className="d-inline-flex align-items-center px-4 py-2 rounded-pill mb-3"
                    style={{
                      background: `${faqs[activeTab].color}15`,
                      border: `1px solid ${faqs[activeTab].color}30`
                    }}
                  >
                    <div 
                      className="rounded-circle me-2"
                      style={{
                        width: '8px',
                        height: '8px',
                        background: faqs[activeTab].color,
                        animation: 'pulse 2s infinite'
                      }}
                    />
                    <small className="fw-bold" style={{ color: faqs[activeTab].color }}>
                      Question {activeTab + 1}
                    </small>
                  </div>
                  <h2 className="fw-bold text-dark mb-3" style={{ fontSize: '2rem' }}>
                    {faqs[activeTab].question}
                  </h2>
                  <p className="text-muted fs-5 mb-0" style={{ lineHeight: '1.6' }}>
                    {faqs[activeTab].answer}
                  </p>
                </div>
                <div className="row g-3 mb-5">
                  {faqs[activeTab].items.map((item, index) => (
                    <div key={index} className="col-md-6">
                      <div 
                        className="p-4 rounded-3 h-100 position-relative overflow-hidden"
                        style={{
                          background: 'white',
                          border: '2px solid #f1f5f9',
                          transition: 'all 0.3s ease',
                          animation: `slideInUp 0.6s ease forwards`,
                          animationDelay: `${index * 0.1}s`,
                          opacity: 0,
                          animationFillMode: 'both'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = faqs[activeTab].color + '40';
                          e.currentTarget.style.transform = 'translateY(-5px)';
                          e.currentTarget.style.boxShadow = `0 10px 30px ${faqs[activeTab].color}20`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#f1f5f9';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <div className="d-flex align-items-start">
                          <div 
                            className="me-3 rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{
                              width: '50px',
                              height: '50px',
                              background: `linear-gradient(135deg, ${faqs[activeTab].color}15, ${faqs[activeTab].color}08)`,
                              fontSize: '20px'
                            }}
                          >
                            {item.icon}
                          </div>
                          <div className="flex-grow-1">
                            <h6 className="fw-bold text-dark mb-1" style={{ fontSize: '16px' }}>
                              {item.text}
                            </h6>
                            <p className="text-muted small mb-0" style={{ fontSize: '13px' }}>
                              {item.desc}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialFaq;